import { Box, Stack, Typography } from "@mui/material";
import AuthenticatedRunAssetImage, { looksLikeImageUrl } from "./AuthenticatedRunAssetImage";
import GenericObjectTable from "./GenericObjectTable";
import {
  formatNumber,
  formatThreshold,
  humanizeColumnHeader,
} from "../utils/decisionFormatters";

function isArrayOfObjects(arr) {
  return Array.isArray(arr) && arr.length > 0 && arr.every((x) => x && typeof x === "object" && !Array.isArray(x));
}

function isArrayOfStrings(arr) {
  return Array.isArray(arr) && arr.length > 0 && arr.every((x) => typeof x === "string");
}

/**
 * @param {{ obj: Record<string, unknown>; runId?: string; depth?: number }} props
 */
function GenericStructuredObject({ obj, runId, depth = 0 }) {
  const entries = Object.entries(obj).filter(([, v]) => v != null);
  entries.sort(([a], [b]) => a.localeCompare(b));
  return (
    <Stack spacing={2}>
      {entries.map(([k, v]) => (
        <Box key={k}>
          <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" gutterBottom>
            {humanizeColumnHeader(k)}
          </Typography>
          <ScalarOrStructured k={k} value={v} runId={runId} depth={depth} />
        </Box>
      ))}
    </Stack>
  );
}

/**
 * @param {{ k?: string; value: unknown; runId?: string; depth?: number }} props
 */
function ScalarOrStructured({ k, value, runId, depth = 0 }) {
  if (k === "threshold" && typeof value === "number") {
    return (
      <Typography variant="body1" fontWeight={500}>
        {formatThreshold(value)}
      </Typography>
    );
  }
  return <GenericStructuredValue value={value} runId={runId} depth={depth} />;
}

/**
 * Renders JSON-like values by type (strings, numbers, arrays, objects) without relying on specific metric names.
 * @param {{ value: unknown; runId?: string; depth?: number }} props
 */
export default function GenericStructuredValue({ value, runId, depth = 0 }) {
  if (value == null) return null;
  if (typeof value === "string") {
    if (runId && looksLikeImageUrl(value)) {
      return <AuthenticatedRunAssetImage runId={runId} url={value} />;
    }
    return (
      <Typography variant="body1" color="text.primary" sx={{ whiteSpace: "pre-wrap", lineHeight: 1.75 }}>
        {value}
      </Typography>
    );
  }
  if (typeof value === "number") {
    return (
      <Typography component="span" variant="body1">
        {formatNumber(value)}
      </Typography>
    );
  }
  if (typeof value === "boolean") {
    return <Typography component="span">{value ? "Yes" : "No"}</Typography>;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return null;
    if (isArrayOfStrings(value)) {
      return (
        <Box component="ul" sx={{ m: 0, pl: 2.5, my: 0.5 }}>
          {value.map((s, i) => (
            <li key={i}>
              <Typography variant="body2" component="span" lineHeight={1.75}>
                {s}
              </Typography>
            </li>
          ))}
        </Box>
      );
    }
    if (isArrayOfObjects(value)) {
      return <GenericObjectTable rows={value} />;
    }
    return (
      <Stack spacing={1} sx={{ pl: depth > 0 ? 1 : 0 }}>
        {value.map((v, i) => (
          <GenericStructuredValue key={i} value={v} runId={runId} depth={depth + 1} />
        ))}
      </Stack>
    );
  }
  if (typeof value === "object") {
    return <GenericStructuredObject obj={value} runId={runId} depth={depth} />;
  }
  return null;
}
